import { Sparkles } from "lucide-react";
import { Button } from "~/components/ui/button";
import { AppIcon } from "~/components/ui/dynamic-icon";
import { TextOverflow } from "~/components/ui/text-overflow";
import { cn } from "~/lib/utils";
import type { AgentStarterPrompt } from "~/services/agents";
import { NEXTGEN_CHAT_EVENT, trackNextgenChatEvent } from "./analytics";

interface StarterPromptsProps {
  prompts: AgentStarterPrompt[];
  onPromptClick: (text: string) => void;
  // "chips" (default) renders today's pill row. "grid" renders the larger
  // 2-column card grid shown inside the expanded guide card.
  variant?: "chips" | "grid";
}

const StarterPromptButton = ({
  prompt,
  onPromptClick,
}: {
  prompt: AgentStarterPrompt;
  onPromptClick: (text: string) => void;
}) => (
  <Button
    variant="outline"
    size="sm"
    onClick={() => {
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.PRESET_CLICKED,
        {},
        { prompt: prompt.text, prompt_id: prompt.id, position: prompt.position }
      );
      onPromptClick(prompt.text);
    }}
    className="min-w-0 flex-1 justify-start gap-2 rounded-full text-sm hover:border-muted-foreground hover:bg-transparent dark:hover:bg-transparent"
  >
    {prompt.icon && (
      <AppIcon name={prompt.icon} fallback={Sparkles} className="size-4 shrink-0 text-foreground" />
    )}
    <TextOverflow
      autoTooltip
      delayDuration={700}
      className={cn("text-foreground", prompt.title ? "font-semibold" : "font-normal")}
    >
      {prompt.title ?? prompt.text}
    </TextOverflow>
  </Button>
);

const StarterPromptCard = ({
  prompt,
  onPromptClick,
}: {
  prompt: AgentStarterPrompt;
  onPromptClick: (text: string) => void;
}) => (
  <button
    type="button"
    onClick={() => onPromptClick(prompt.text)}
    className={cn(
      "flex min-w-0 cursor-pointer items-center gap-3 rounded-lg border p-4 text-left transition-colors",
      "hover:border-muted-foreground"
    )}
  >
    {prompt.icon && (
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
        <AppIcon name={prompt.icon} fallback={Sparkles} className="size-4 text-foreground" />
      </span>
    )}
    <span className="min-w-0 flex-1">
      <TextOverflow
        autoTooltip
        delayDuration={700}
        className="block w-full font-semibold text-foreground"
      >
        {prompt.title ?? prompt.text}
      </TextOverflow>
      {prompt.title && (
        <TextOverflow
          autoTooltip
          delayDuration={700}
          lines={2}
          className="w-full text-sm text-muted-foreground"
        >
          {prompt.text}
        </TextOverflow>
      )}
    </span>
  </button>
);

export const StarterPrompts = ({
  prompts,
  onPromptClick,
  variant = "chips",
}: StarterPromptsProps) => {
  const sorted = [...prompts].sort((a, b) => a.position - b.position);

  if (variant === "grid") {
    return (
      <div className="grid w-full grid-cols-2 gap-3">
        {sorted.map((prompt) => (
          <StarterPromptCard key={prompt.id} prompt={prompt} onPromptClick={onPromptClick} />
        ))}
      </div>
    );
  }

  const splitIndex = sorted.length <= 3 ? sorted.length : Math.floor(sorted.length / 2);
  const topRow = sorted.slice(0, splitIndex);
  const bottomRow = sorted.slice(splitIndex);

  return (
    <div className="flex w-full max-w-2xl flex-col gap-2">
      <div className="flex gap-2">
        {topRow.map((prompt) => (
          <StarterPromptButton key={prompt.id} prompt={prompt} onPromptClick={onPromptClick} />
        ))}
      </div>
      {bottomRow.length > 0 && (
        <div className="flex gap-2">
          {bottomRow.map((prompt) => (
            <StarterPromptButton key={prompt.id} prompt={prompt} onPromptClick={onPromptClick} />
          ))}
        </div>
      )}
    </div>
  );
};
